/**
 * PlatformContext — single entry point for all platform service clients.
 *
 * One transport, one context. Provides identity, models, and storage clients,
 * each calling with the console's token (rpc-web console-token.ts).
 *
 * The platformClient singleton is also available via @/lib/platform-client
 * for use outside React (e.g., route loaders).
 */

import { useT } from '@foir/i18n';
import { createContext, useContext } from "react";
import type { PlatformClient } from "@foir/rpc-web/platform";
import type { ReactNode } from "react";

const PlatformCtx = createContext<PlatformClient | null>(null);

export function PlatformProvider({
  client,
  children,
}: {
  /** App-configured platform client (base URL, transport, auth). */
  client: PlatformClient;
  children: ReactNode;
}) {
  return <PlatformCtx.Provider value={client}>{children}</PlatformCtx.Provider>;
}

export function usePlatformClient(): PlatformClient {
  const t = useT();
  const client = useContext(PlatformCtx);
  if (!client) {
    throw new Error(t('auth.useplatformclient_must_be_used_within_a_platformprovider', 'usePlatformClient must be used within a PlatformProvider'));
  }
  return client;
}

/** Convenience hooks for individual domain clients. */
export function useAgentsClient() {
  return usePlatformClient().agents;
}

export function useBillingClient() {
  return usePlatformClient().billing;
}

export function useIdentityClient() {
  return usePlatformClient().identity;
}

export function useModelsClient() {
  return usePlatformClient().models;
}

export function useRecordsClient() {
  return usePlatformClient().records;
}

export function useAccessClient() {
  return usePlatformClient().access;
}

export function useAuthorizationClient() {
  return usePlatformClient().authorization;
}

export function useStorageClient() {
  return usePlatformClient().storage;
}

export function useConfigsClient() {
  return usePlatformClient().configs;
}

export function useEmailClient() {
  return usePlatformClient().email;
}

export function useNotificationsClient() {
  return usePlatformClient().notifications;
}

export function useSettingsClient() {
  return usePlatformClient().settings;
}

export function useOperationsClient() {
  return usePlatformClient().operations;
}

export function useRunsClient() {
  return usePlatformClient().runs;
}

export function useHooksClient() {
  return usePlatformClient().hooks;
}

export function useSchedulesClient() {
  return usePlatformClient().schedules;
}

export function useAnalyticsClient() {
  return usePlatformClient().analytics;
}

export function useQueueClient() {
  return usePlatformClient().queue;
}

export function useAppsClient() {
  return usePlatformClient().apps;
}

/**
 * The app registry behind the marketplace. Global reference data: nothing on it
 * takes a tenant or a project, and reads are session-only because browsing
 * happens before a user has chosen where to install anything.
 */
export function useMarketplaceClient() {
  return usePlatformClient().marketplace;
}

export function usePlacementsClient() {
  return usePlatformClient().placements;
}

export function useSecretsClient() {
  return usePlatformClient().secrets;
}

export function usePlatformSettingsClient() {
  return usePlatformClient().platformSettings;
}
